import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getAsset, createBooking, addToCart, generateSignedUrl } from '../utils/api';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { Check, ChevronLeft, ChevronRight, Download, ShoppingCart } from 'lucide-react';
import NavigationBar from '@/components/NavigationBar';
import Footer from '@/components/Footer';
import FAQSection from '@/components/FAQSection';

const AssetDetailPage = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [asset, setAsset] = useState(null);
  const [tickets, setTickets] = useState(1);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageCategory, setImageCategory] = useState('exterior');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  
  // Store previous page state before leaving
  useEffect(() => {
    // Save current scroll position before navigating away
    return () => {
      if (location.state?.from) {
        sessionStorage.setItem('previousPageState', JSON.stringify({
          scrollY: window.scrollY,
          from: location.state.from
        }));
      }
    };
  }, [location]);
  
  const handleBack = () => {
    // Use browser's back button to maintain state
    navigate(-1);
  };

  useEffect(() => {
    loadAsset();
  }, [id]);

  const loadAsset = async () => {
    try {
      const response = await getAsset(id);
      setAsset(response.data);
    } catch (error) {
      toast.error('Failed to load asset');
    } finally {
      setLoading(false);
    }
  };

  const handleExpressInterest = async () => {
    if (!user) {
      toast.error('Please login to express interest');
      navigate('/login');
      return;
    }

    setBooking(true);
    try {
      await addToCart({ asset_id: id, tickets: tickets });
      setShowSuccessModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to cart');
    } finally {
      setBooking(false);
    }
  };
  
  const handleDownloadBrochure = async () => {
    // Check if brochure is available
    if (!asset?.brochure_url) {
      toast.error('Brochure is not available for this asset');
      return;
    }

    try {
      // Show loading toast
      const loadingToast = toast.loading('Preparing brochure download...');
      
      // Generate signed URL for the PDF
      const response = await generateSignedUrl(asset.brochure_url);
      const signedUrl = response.data.signed_url;
      
      // Dismiss loading toast
      toast.dismiss(loadingToast);
      
      // Create a temporary link element for download
      const link = document.createElement('a');
      link.href = signedUrl;
      link.download = `${asset.name.replace(/\s+/g, '-')}-Brochure.pdf`;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Brochure download started!');
      
      // Log download event (optional analytics)
      console.log('Brochure downloaded:', asset.name);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Unable to download brochure. Please try again later.');
    }
  };
  
  const handlePrevImage = () => {
    const images = getFilteredImages();
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };
  
  const handleNextImage = () => {
    const images = getFilteredImages();
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };
  
  const getFilteredImages = () => {
    if (!asset) return [];
    // Use separate exterior_images and interior_images arrays
    if (imageCategory === 'exterior') {
      return asset.exterior_images || [];
    } else {
      return asset.interior_images || [];
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold"></div>
      </div>
    );
  }

  if (!asset) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <p>Asset not found</p>
      </div>
    );
  }

  const availableTickets = asset.total_tickets - asset.sold_tickets;
  const filteredImages = getFilteredImages();
  const currentImage = filteredImages[currentImageIndex] || asset.images[0];

  return (
    <div className="min-h-screen bg-cream">
      {/* Navigation Bar - Same as Homepage */}
      <NavigationBar user={user} />
      
      {/* Back and Contact Us Buttons */}
      <div className="max-w-7xl mx-auto px-6 pt-8 pb-4">
        <div className="flex items-center justify-between">
          <Button 
            onClick={handleBack}
            className="border border-black bg-white text-black hover:bg-black hover:text-white transition-all"
          >
            ← Back
          </Button>
          <Button 
            onClick={() => document.getElementById('get-in-touch')?.scrollIntoView({ behavior: 'smooth' })}
            className="border border-black bg-white text-black hover:bg-black hover:text-white transition-all"
          >
            Contact Us
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-2 gap-12">
          {/* Image Gallery */}
          <div>
            {/* Main Image with Navigation Arrows */}
            <div className="relative mb-4">
              <img src={currentImage} alt={asset.name} className="w-full h-96 object-cover rounded-lg shadow-2xl" />
              
              {/* Left Arrow */}
              <button
                onClick={handlePrevImage}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/75 text-white p-3 rounded-full transition-all"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              
              {/* Right Arrow */}
              <button
                onClick={handleNextImage}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/75 text-white p-3 rounded-full transition-all"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>
            
            {/* Category Tabs */}
            <div className="flex space-x-4 mb-4">
              <button
                onClick={() => { setImageCategory('exterior'); setCurrentImageIndex(0); }}
                className={`px-6 py-2 font-semibold uppercase tracking-wide transition-all ${
                  imageCategory === 'exterior' 
                    ? 'bg-black text-white' 
                    : 'bg-white text-black border border-black hover:bg-black hover:text-white'
                }`}
              >
                Exterior
              </button>
              <button
                onClick={() => { setImageCategory('interior'); setCurrentImageIndex(0); }}
                className={`px-6 py-2 font-semibold uppercase tracking-wide transition-all ${
                  imageCategory === 'interior' 
                    ? 'bg-black text-white' 
                    : 'bg-white text-black border border-black hover:bg-black hover:text-white'
                }`}
              >
                Interior
              </button>
            </div>
            
            {/* Thumbnail Grid */}
            <div className="grid grid-cols-4 gap-4">
              {filteredImages.map((img, idx) => (
                <img 
                  key={idx} 
                  src={img} 
                  alt={`${asset.name} ${imageCategory} ${idx + 1}`} 
                  onClick={() => setCurrentImageIndex(idx)}
                  className={`w-full h-20 object-cover rounded-lg cursor-pointer transition-all ${
                    currentImageIndex === idx ? 'ring-4 ring-gold' : 'hover:opacity-75'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Product Details */}
          <div>
            <h1 className="text-4xl font-bold mb-4" data-testid="asset-name">{asset.name}</h1>
            <p className="text-xl text-gray mb-2">{asset.description}</p>
            {asset.cities && asset.cities.length > 0 && (
              <p className="text-sm text-gray mb-8">
                <span className="font-semibold">Available in:</span> {asset.cities.join(', ')}
              </p>
            )}

            {/* Booking Card */}
            <div className="bg-white p-6 rounded-lg shadow-lg mb-6">
              <div className="flex justify-between items-center mb-4">
                <span className="text-3xl font-bold text-gold">₹{asset.price_per_ticket.toLocaleString()}</span>
                <span className="text-gray">per ticket</span>
              </div>

              <div className="mb-6">
                <div className="flex justify-between text-sm mb-2">
                  <span>Tickets Sold</span>
                  <span className="font-semibold">{asset.sold_tickets}/{asset.total_tickets}</span>
                </div>
                <Progress value={(asset.sold_tickets / asset.total_tickets) * 100} className="h-2" />
                <p className="text-sm text-gray mt-2">{availableTickets} tickets available</p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">
                  Number of Tickets <span className="text-xs text-gray">(1 ticket = 30 days usage/year)</span>
                </label>
                <Input
                  type="number"
                  min="1"
                  max={availableTickets}
                  value={tickets}
                  onChange={(e) => setTickets(parseInt(e.target.value))}
                  data-testid="tickets-input"
                />
                <div className="flex justify-between items-center mt-2">
                  <p className="text-sm text-gray">
                    Total: ₹{(asset.price_per_ticket * tickets).toLocaleString()}
                  </p>
                  <p className="text-sm font-semibold text-gold">
                    {tickets * 30} days/year
                  </p>
                </div>
              </div>

              <Button
                onClick={handleExpressInterest}
                disabled={availableTickets === 0 || booking}
                className="w-full bg-black text-white border-2 border-black hover:bg-white hover:text-black py-6 text-lg mb-3 flex items-center justify-center gap-2"
                data-testid="express-interest-btn"
              >
                {booking ? (
                  'Adding to Cart...'
                ) : availableTickets === 0 ? (
                  'Sold Out'
                ) : (
                  <>
                    <ShoppingCart className="w-5 h-5" />
                    Express Interest
                  </>
                )}
              </Button>
              
              <Button
                onClick={handleDownloadBrochure}
                disabled={!asset?.brochure_url}
                className={`w-full py-6 text-lg ${
                  asset?.brochure_url
                    ? 'bg-white text-black border-2 border-black hover:bg-black hover:text-white'
                    : 'bg-gray-300 text-gray-500 border-2 border-gray-300 cursor-not-allowed'
                }`}
                title={!asset?.brochure_url ? 'Brochure not available for this asset' : 'Download PDF brochure'}
              >
                <Download className="w-5 h-5 mr-2" />
                {asset?.brochure_url ? 'Download Full Brochure' : 'Brochure Not Available'}
              </Button>
            </div>

            {asset.features && asset.features.length > 0 && (
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <h3 className="text-xl font-bold mb-4">Features</h3>
                <ul className="space-y-2">
                  {asset.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center">
                      <Check className="w-5 h-5 text-gold mr-2" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* FAQ Section - Dynamic from Admin Portal */}
        <div className="mt-12">
          <FAQSection 
            category="individual-assets" 
            title="Frequently Asked Questions" 
          />
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6 md:p-8 animate-in fade-in zoom-in duration-200">
            {/* Success Icon */}
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-10 h-10 text-green-600" />
            </div>

            {/* Title */}
            <h2 className="text-xl md:text-2xl font-bold text-center mb-3">
              Thank You for Your Interest!
            </h2>

            {/* Message */}
            <p className="text-gray-700 text-center mb-6 leading-relaxed">
              Thank you for your interest in the <span className="font-semibold">{asset?.name}</span>. Our team will reach out to you shortly to assist you. You can view the status of your ticket in the Dashboard.
            </p>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate('/member-dashboard?tab=cart');
                }}
                className="flex-1 bg-black text-white hover:bg-black/90"
              >
                View in Dashboard
              </Button>
              <Button
                onClick={() => setShowSuccessModal(false)}
                variant="outline"
                className="flex-1 border-black text-black hover:bg-black hover:text-white"
              >
                Continue Browsing
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Get In Touch Footer */}
      <div id="get-in-touch">
        <Footer />
      </div>
    </div>
  );
};

export default AssetDetailPage;
